import {
  PropsWithChildren,
  createContext,
  useContext,
  useState
} from "react";
import type { SessionResponseDto } from "@madamuh/domain";
import { ApiError } from "../api/client";
import { fetchCurrentSession, postLineLogin } from "../api/session";

type SessionBootstrapStatus = "idle" | "loading" | "authenticated" | "unauthenticated" | "error";

type SessionBootstrapContextValue = {
  status: SessionBootstrapStatus;
  session: SessionResponseDto | null;
  errorMessage: string | null;
  bootstrapSession: (tenantSlug: string) => Promise<SessionResponseDto | null>;
  lineLoginWithMockToken: (tenantSlug: string, idToken: string) => Promise<SessionResponseDto | null>;
};

const SessionBootstrapContext = createContext<SessionBootstrapContextValue | null>(null);

function isSessionForTenant(session: SessionResponseDto, tenantSlug: string): boolean {
  return session.tenant.tenantSlug === tenantSlug;
}

export function SessionBootstrapProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<SessionBootstrapStatus>("idle");
  const [session, setSession] = useState<SessionResponseDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function bootstrapSession(tenantSlug: string): Promise<SessionResponseDto | null> {
    setStatus("loading");
    setErrorMessage(null);

    try {
      const nextSession = await fetchCurrentSession();

      if (!isSessionForTenant(nextSession, tenantSlug)) {
        setSession(null);
        setStatus("unauthenticated");
        return null;
      }

      setSession(nextSession);
      setStatus("authenticated");
      return nextSession;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setSession(null);
        setStatus("unauthenticated");
        return null;
      }

      setSession(null);
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "Session bootstrap failed");
      return null;
    }
  }

  async function lineLoginWithMockToken(tenantSlug: string, idToken: string): Promise<SessionResponseDto | null> {
    setStatus("loading");
    setErrorMessage(null);

    try {
      const nextSession = await postLineLogin({ tenantSlug, idToken });
      setSession(nextSession);
      setStatus("authenticated");
      return nextSession;
    } catch (error) {
      setSession(null);
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "LINE login failed");
      return null;
    }
  }

  return (
    <SessionBootstrapContext.Provider
      value={{
        status,
        session,
        errorMessage,
        bootstrapSession,
        lineLoginWithMockToken
      }}
    >
      {children}
    </SessionBootstrapContext.Provider>
  );
}

export function useSessionBootstrap() {
  const context = useContext(SessionBootstrapContext);

  if (!context) {
    throw new Error("useSessionBootstrap must be used within SessionBootstrapProvider");
  }

  return context;
}
